/**
 * STRYDE V7 pre-landers (layout/pl7-prelander.liquid only): clips, dial demo, campaign pass-through,
 * sticky bars and the buy module.
 * Cart adds follow the theme's own pattern (assets/stryde-cart.js): one POST to cart/add.js with an `items` array and the
 * cart drawer section, announced to Horizon with a CartLinesUpdateEvent so the existing drawer re-renders and opens.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobile = window.matchMedia('(max-width: 600px)');
const root = window.Shopify?.routes?.root ?? '/';
const DRAWER_SECTION = 'cart-drawer-section';

/* ---------- helpers ---------- */
const readJson = (el) => {
  try {
    return JSON.parse(el ? el.textContent : 'null');
  } catch {
    return null;
  }
};
/** "Black:#0B0B0B, White:#F2F2F2" → { Black: '#0B0B0B', … } */
const pairs = (text) =>
  Object.fromEntries(
    String(text || '')
      .split(',')
      .map((p) => p.split(':').map((x) => x.trim()))
      .filter((p) => p[0] && p[1])
  );
/** Values of the product option at the given position, in Shopify order. */
const values = (data, key) => (data.at[key] >= 0 ? data.options[data.at[key]] || [] : []);
/** Variant for a { size, side, colour } pick, matched on the option values. */
const findVariant = (data, pick) =>
  data.variants.find((v) => ['size', 'side', 'colour'].every((k) => data.at[k] < 0 || v.o[data.at[k]] === pick[k]));
const sideLabel = (value, strip) => (strip ? value.replace(strip, '').trim() : value);
const scrollTo = (el) => el?.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });

/* ---------- campaign data to the regular product page ---------- */
const CLICK_IDS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'dclid', 'ttclid', 'msclkid', 'twclid', 'li_fat_id', 'epik', 'sccid', 'rdt_cid', 'irclickid'];
const isCampaign = (key) => /^utm_/i.test(key) || CLICK_IDS.includes(key.toLowerCase());

/** Adds the campaign parameters of this page to a link. The link's own parameters (e.g. variant) win. */
const withCampaign = (href) => {
  const url = new URL(href, location.origin);
  new URLSearchParams(location.search).forEach((value, key) => {
    if (isCampaign(key) && !url.searchParams.has(key)) url.searchParams.set(key, value);
  });
  return url.origin === location.origin ? url.pathname + url.search + url.hash : url.href;
};
document.querySelectorAll('a[data-pl7-pdp]').forEach((a) => {
  a.href = withCampaign(a.getAttribute('href'));
});

/* ---------- cart ---------- */
const openCart = () => {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else location.href = root + 'cart';
};
document.querySelectorAll('[data-pl7-cart]').forEach((b) => b.addEventListener('click', openCart));

/** Refreshes the header count from the real cart and returns that cart (null when it cannot be read). */
const updateCount = async () => {
  try {
    const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    document.querySelectorAll('[data-pl7-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
    return cart;
  } catch {
    return null;
  }
};

/** One line per variant: identical sleeves become one line with a quantity. */
const toItems = (variantIds) => {
  const map = new Map();
  variantIds.forEach((id) => map.set(id, (map.get(id) || 0) + 1));
  return [...map].map(([id, quantity]) => ({ id, quantity }));
};

/**
 * Adds the items in one cart/add.js request. With `announce`, Horizon gets a CartLinesUpdateEvent so the existing cart
 * drawer re-renders from the returned section and opens (auto-open on 'add'). Throws with Shopify's message on failure.
 * @param {{id: number, quantity: number}[]} items
 * @param {Element} source element the event is dispatched from
 * @param {boolean} announce
 */
async function addToCart(items, source, announce = true) {
  const deferred = announce ? CartLinesUpdateEvent.createPromise() : null;
  if (deferred) {
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }
  let data;
  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: DRAWER_SECTION, sections_url: location.pathname }),
    });
    data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');
  } catch (error) {
    // Without sections Horizon refetches the drawer, so it always shows the real cart.
    deferred?.resolve({ detail: { source: 'pl7-prelander', didError: true } });
    throw error;
  }
  // The add succeeded. cart/add.js only returns the added lines, so the full cart (with currency) comes from cart.js.
  const cart = await updateCount();
  let eventCart;
  try {
    eventCart = cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined;
  } catch {
    eventCart = undefined;
  }
  deferred?.resolve({
    cart: eventCart,
    detail: { sections: data.sections, items: data.items, itemCount: cart?.item_count, source: 'pl7-prelander', didError: false },
  });
  return data;
}

/* ---------- clips: play at 40% in view, pause outside; with reduced motion only on request ---------- */
const clips = document.querySelectorAll('video[data-pl7-clip]');
if (reduce.matches) {
  clips.forEach((v) => v.setAttribute('controls', ''));
} else if (clips.length && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) target.play().catch(() => {});
        else target.pause();
      });
    },
    { root: null, threshold: 0.4 }
  );
  clips.forEach((v) => {
    v.muted = true;
    io.observe(v);
  });
}

/* ---------- mobile: links to the buy module land on the sleeve choice, not on the large gallery photo ---------- */
document.addEventListener('click', (event) => {
  const a = event.target.closest?.('.pl7 a[href="#buy"], .pl7 a[href="#pl7-choose"]');
  if (!a) return;
  const target = mobile.matches ? document.getElementById('pl7-choose') : document.getElementById('buy');
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
});

/* ---------- dial demo ---------- */
document.querySelectorAll('[data-pl7-dial]').forEach((demo) => {
  const tabs = [...demo.querySelectorAll('.scenario-tabs button')];
  const track = demo.querySelector('.tension-track');
  tabs.forEach((tab) =>
    tab.addEventListener('click', () => {
      tabs.forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
      demo.querySelector('[data-pl7-number]').textContent = tab.dataset.number;
      demo.querySelector('[data-pl7-title]').textContent = tab.dataset.title;
      demo.querySelector('[data-pl7-text]').textContent = tab.dataset.text;
      if (track) track.dataset.level = tab.dataset.level;
    })
  );
});

/* ---------- sticky bar ---------- */
class Pl7Sticky extends HTMLElement {
  connectedCallback() {
    this.data = readJson(this.querySelector('[data-pl7-product]'));
    if (!this.data) return;
    this.mode = this.dataset.mode;
    this.inner = this.querySelector('.sticky-in');
    this.pick = { colour: values(this.data, 'colour')[0] || null, side: null, size: null };
    this.btn = this.querySelector('[data-pl7-st-btn]');
    this.baseHref = this.btn.getAttribute('href');
    this.busy = false;
    this.buildOptions();
    this.watch();
    this.btn.addEventListener('click', (e) => this.onClick(e));
  }

  buildOptions() {
    const host = this.querySelector('[data-pl7-st-opts]');
    const sw = pairs(this.dataset.swatches);
    const groups = [
      ['colour', this.dataset.tColour],
      ['side', this.dataset.tSide],
      ['size', this.dataset.tSize],
    ];
    host.innerHTML = '';
    groups.forEach(([key, label]) => {
      const list = values(this.data, key);
      if (!list.length) return;
      const g = document.createElement('div');
      g.className = 'sg2';
      g.dataset.k = key;
      const small = document.createElement('small');
      small.textContent = label;
      if (key === 'colour') {
        const i = document.createElement('i');
        i.dataset.cn = '';
        small.append(i);
      }
      const seg = document.createElement('div');
      seg.className = 'seg2' + (key === 'colour' ? ' col' : '');
      seg.setAttribute('role', 'group');
      seg.setAttribute('aria-label', label);
      list.forEach((value) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.dataset.v = value;
        if (key === 'colour') {
          b.style.background = sw[value] || '#ccc';
          b.setAttribute('aria-label', value);
        } else b.textContent = key === 'side' ? sideLabel(value, this.dataset.sideStrip) : value;
        b.addEventListener('click', () => {
          this.pick[key] = value;
          g.classList.remove('err');
          this.render();
        });
        seg.append(b);
      });
      g.append(small, seg);
      host.append(g);
    });
    this.render();
  }

  render() {
    this.querySelectorAll('.sg2').forEach((g) => {
      g.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(this.pick[g.dataset.k] === b.dataset.v)));
    });
    const cn = this.querySelector('[data-cn]');
    if (cn) cn.textContent = this.pick.colour || '';
    const img = this.querySelector('.pi img');
    const key = String(this.pick.colour || '').toLowerCase() === 'white' ? 'imgWhite' : 'imgBlack';
    if (img && this.dataset[key]) {
      img.src = this.dataset[key];
      img.removeAttribute('srcset');
    }
    if (this.mode === 'adv') {
      const v = this.pick.side && this.pick.size ? findVariant(this.data, this.pick) : null;
      const url = new URL(this.baseHref, location.origin);
      if (v && v.a) url.searchParams.set('variant', v.id);
      else url.searchParams.delete('variant');
      this.btn.href = withCampaign(url.pathname + url.search);
    }
  }

  async onClick(e) {
    // Advertorial: a plain link to the product page. Product page on mobile: jump to the sleeve choice (document handler).
    if (this.mode === 'adv' || mobile.matches) return;
    e.preventDefault();
    e.stopPropagation();
    if (this.busy) return;
    let ok = true;
    ['side', 'size'].forEach((k) => {
      if (!this.pick[k]) {
        this.querySelector(`.sg2[data-k="${k}"]`)?.classList.add('err');
        ok = false;
      }
    });
    if (!ok) return;
    const v = findVariant(this.data, this.pick);
    if (!v || !v.a) {
      this.querySelector('.sg2[data-k="size"]')?.classList.add('err');
      return;
    }
    this.busy = true;
    this.btn.setAttribute('aria-busy', 'true');
    try {
      await addToCart(toItems([v.id]), this.btn);
    } catch (err) {
      console.error('[pl7] add to cart failed', err);
    } finally {
      this.busy = false;
      this.btn.removeAttribute('aria-busy');
    }
  }

  /** Advertorial: show once the hero has scrolled away. Product page: hide while the hero CTA or buy buttons are in view. */
  watch() {
    const targets =
      this.mode === 'adv'
        ? [...document.querySelectorAll('[data-pl7-hero]')]
        : [...document.querySelectorAll('[data-pl7-keep-sticky-off]')];
    if (!targets.length || !('IntersectionObserver' in window)) return;
    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target, e.isIntersecting));
        const anyVisible = [...visible.values()].some(Boolean);
        const pastTop = this.mode !== 'adv' || targets[0].getBoundingClientRect().bottom < 0;
        const on = !anyVisible && pastTop;
        this.classList.toggle('on', on);
        this.inner.toggleAttribute('inert', !on);
      },
      { root: null, threshold: 0 }
    );
    targets.forEach((t) => io.observe(t));
  }
}
if (!customElements.get('pl7-sticky')) customElements.define('pl7-sticky', Pl7Sticky);

/* ---------- buy module ---------- */
class Pl7Buy extends HTMLElement {
  connectedCallback() {
    this.data = readJson(this.querySelector('[data-pl7-product]'));
    if (!this.data) return;
    this.picks = [];
    this.busy = false;
    this.sw = pairs(this.dataset.swatches);
    this.colourThumb = pairs(this.dataset.colourThumbs);
    this.cfg = this.querySelector('[data-pl7-cfg]');
    this.msg = this.querySelector('[data-pl7-msg]');
    this.tiers = [...this.querySelectorAll('.tier')];
    this.thumbs = [...this.querySelectorAll('[data-pl7-thumbs] button')];
    this.tiers.forEach((t) => t.addEventListener('click', () => this.pickTier(t)));
    this.thumbs.forEach((b) => b.addEventListener('click', () => this.showThumb(b)));
    this.cfg.addEventListener('click', (e) => this.onPick(e));
    const sgBtn = this.querySelector('[data-pl7-sg-btn]');
    const sg = sgBtn && this.querySelector('#' + sgBtn.getAttribute('aria-controls'));
    sgBtn?.addEventListener('click', () => {
      const on = !sg.classList.contains('on');
      sg.classList.toggle('on', on);
      sgBtn.setAttribute('aria-expanded', String(on));
    });
    this.querySelector('[data-pl7-add]').addEventListener('click', (e) => this.submit(e.currentTarget, false));
    this.querySelector('[data-pl7-buy-now]').addEventListener('click', (e) => this.submit(e.currentTarget, true));
    if (this.tiers[0]) this.pickTier(this.tiers[0]);
  }

  pickTier(tier) {
    this.tiers.forEach((t) => t.setAttribute('aria-pressed', String(t === tier)));
    this.querySelector('[data-pl7-price]').textContent = tier.dataset.total;
    this.querySelector('[data-pl7-save]').textContent = tier.dataset.save || '';
    this.n = Number(tier.dataset.n) || 1;
    const colour = values(this.data, 'colour').includes('Black') ? 'Black' : values(this.data, 'colour')[0] || null;
    for (let i = 0; i < this.n; i++) if (!this.picks[i]) this.picks[i] = { colour, side: null, size: null };
    // Two or more sleeves start as one left + one right, without overwriting a side that was already chosen.
    const sides = values(this.data, 'side');
    if (this.n >= 2 && !this.picks[1].side && sides.length >= 2) {
      if (!this.picks[0].side) this.picks[0].side = sides[0];
      this.picks[1].side = sides.find((s) => s !== this.picks[0].side);
    }
    this.msg.hidden = true;
    this.draw();
  }

  draw() {
    const strip = this.dataset.sideStrip;
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const btn = (key, value, pick, label) => {
      const pressed = pick[key] === value;
      let extra = '';
      if (key === 'size' && pick.side) {
        const v = findVariant(this.data, { ...pick, size: value });
        if (!v || !v.a) extra = ` data-unavailable aria-description="${esc(this.dataset.tUnavailable)}"`;
      }
      if (key === 'colour')
        return `<button type="button" data-k="colour" data-v="${esc(value)}" aria-pressed="${pressed}" aria-label="${esc(value)}" style="background:${esc(this.sw[value] || '#ccc')}"></button>`;
      return `<button type="button" data-k="${key}" data-v="${esc(value)}" aria-pressed="${pressed}"${extra}>${esc(label)}</button>`;
    };
    let html = '';
    for (let i = 0; i < this.n; i++) {
      const p = this.picks[i];
      const summary = [p.colour, p.side && sideLabel(p.side, strip), p.size].filter(Boolean).join(' · ');
      const name = `${this.dataset.tSleeve} ${i + 1}`;
      html +=
        `<div class="cfg-row" data-i="${i}" role="group" aria-label="${esc(name)}"><div class="cfg-h">${esc(name)}<small aria-live="polite">${esc(summary)}</small></div>` +
        `<div class="sw" data-k="colour">${values(this.data, 'colour').map((v) => btn('colour', v, p)).join('')}</div>` +
        `<div class="pills" data-k="side">${values(this.data, 'side').map((v) => btn('side', v, p, sideLabel(v, strip))).join('')}</div>` +
        `<div class="pills" data-k="size">${values(this.data, 'size').map((v) => btn('size', v, p, v)).join('')}</div></div>`;
    }
    this.cfg.innerHTML = html;
  }

  onPick(e) {
    const b = e.target.closest('button[data-v]');
    if (!b) return;
    const i = Number(b.closest('.cfg-row').dataset.i);
    this.picks[i][b.dataset.k] = b.dataset.v;
    this.draw();
    this.cfg.querySelector(`.cfg-row[data-i="${i}"] [data-k="${b.dataset.k}"] [data-v="${CSS.escape(b.dataset.v)}"]`)?.focus();
    if (i === 0 && b.dataset.k === 'colour') {
      const n = Number(this.colourThumb[b.dataset.v]);
      if (this.thumbs[n - 1]) this.showThumb(this.thumbs[n - 1]);
    }
  }

  showThumb(b) {
    this.thumbs.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    const main = this.querySelector('[data-pl7-main] img');
    if (!main) return;
    main.src = b.dataset.src;
    main.srcset = b.dataset.srcset || '';
    main.alt = b.dataset.alt || '';
  }

  /** Checks every active sleeve; returns the variant ids or null after marking the problems. */
  validate() {
    let first = null;
    const ids = [];
    for (let i = 0; i < this.n; i++) {
      const p = this.picks[i];
      const row = this.cfg.querySelector(`[data-i="${i}"]`);
      const small = row.querySelector('small');
      let problem = !p.side && !p.size ? this.dataset.tBoth : !p.side ? this.dataset.tSide : !p.size ? this.dataset.tSize : '';
      const v = problem ? null : findVariant(this.data, p);
      if (!problem && (!v || !v.a)) problem = this.dataset.tUnavailable;
      row.classList.toggle('err', Boolean(problem));
      if (problem) {
        small.textContent = problem;
        first = first || { row, key: !p.side ? 'side' : 'size' };
      } else ids.push(v.id);
    }
    if (first) {
      scrollTo(first.row);
      first.row.querySelector(`[data-k="${first.key}"] button`)?.focus({ preventScroll: true });
      return null;
    }
    return ids;
  }

  async submit(button, checkout) {
    if (this.busy) return;
    this.msg.hidden = true;
    const ids = this.validate();
    if (!ids) return;
    this.busy = true;
    button.setAttribute('aria-busy', 'true');
    try {
      // Buy now leaves the page, so the drawer is not opened first.
      await addToCart(toItems(ids), button, !checkout);
      if (checkout) location.href = root + 'checkout';
    } catch (err) {
      console.error('[pl7] add to cart failed', err);
      this.msg.textContent = err?.message && err.message !== 'Add to cart failed' ? err.message : this.dataset.tError;
      this.msg.hidden = false;
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('pl7-buy')) customElements.define('pl7-buy', Pl7Buy);
